import { getCatalogLocale, getCatalogVersion, lookup } from './catalog';
import { DEFAULT_LOCALE } from './locale';
import { isDisplayKey, isSentenceArrayKey, NEVER_TRANSLATE_KEYS } from './displayKeys';

// Memoize per source object. A WeakMap keyed on the input means repeated renders
// of the same page-data module reuse one localized tree and add no GC pressure.
// It is reset whenever the catalog version changes — keyed on version rather than
// locale so that swapping in a new dictionary for the SAME locale (e.g. a hot
// catalog reload) invalidates stale memoized translations too.
let memo = new WeakMap();
let memoVersion = null;

function ensureMemoForVersion(version) {
  if (memoVersion !== version) {
    memo = new WeakMap();
    memoVersion = version;
  }
}

export function t(englishText) {
  if (typeof englishText !== 'string') return englishText;
  if (getCatalogLocale() === DEFAULT_LOCALE) return englishText;
  return lookup(englishText) || englishText;
}

function localizeValue(value, keyHint) {
  // A denylisted key shields its ENTIRE subtree, not just a leaf string. Config
  // blobs like `weights` or `routes` may contain nested objects whose keys
  // collide with display-key names; translating inside them would corrupt data.
  if (keyHint && NEVER_TRANSLATE_KEYS.has(keyHint)) return value;
  if (typeof value === 'string') {
    return keyHint && isDisplayKey(keyHint) ? t(value) : value;
  }
  if (Array.isArray(value)) {
    const translateStrings = keyHint && isSentenceArrayKey(keyHint);
    return value.map((item) =>
      typeof item === 'string'
        ? (translateStrings ? t(item) : item)
        : localizeValue(item, undefined)
    );
  }
  if (value && typeof value === 'object') {
    // Only plain objects are walked. React elements, Dates, and class instances
    // are returned untouched — cloning them would break rendering.
    if (value.$$typeof || !isPlainObject(value)) return value;
    const out = {};
    for (const k of Object.keys(value)) {
      out[k] = localizeValue(value[k], k);
    }
    return out;
  }
  return value;
}

function isPlainObject(value) {
  const proto = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
}

export function localizeData(value) {
  const locale = getCatalogLocale();
  if (locale === DEFAULT_LOCALE) return value;
  if (!value || typeof value !== 'object') return localizeValue(value, undefined);

  ensureMemoForVersion(getCatalogVersion());
  if (memo.has(value)) return memo.get(value);
  const result = localizeValue(value, undefined);
  memo.set(value, result);
  return result;
}
